<!-- Chequered flag — across the line. The result, the lap time, and the way
     to the champagne when you won. -->
<template>
  <IonPage>
    <AppHeader :title="COPY.title" :back="backTo" />

    <IonContent :fullscreen="true">
      <div class="ion-padding flag-body">
        <template v-if="lap">
          <div class="flag" :class="{ still: still }" aria-hidden="true"><span v-for="i in 6" :key="i" /></div>
          <p class="banner" :class="{ 'banner--win': won }">{{ lap.result }}</p>
          <div class="flag-stats">
            <StatTile :label="COPY.lapTime" :value="lap.lapTime" />
            <StatTile :label="COPY.position" :value="lap.position" />
          </div>
          <p v-if="isBest" class="flag-note">{{ COPY.newBest }}</p>
          <p v-else-if="!won" class="flag-note">{{ COPY.tryAgain }}</p>
          <ActionButton v-if="won" :label="COPY.champ" kind="primary" data-cta="champ-btn" @click="toChampagne" />
          <ActionButton :label="COPY.again" :kind="won ? 'secondary' : 'primary'" data-cta="again-btn" @click="raceAgain" />
        </template>
        <EmptyState v-else :icon="flagOutline" :title="COPY.missingTitle" :body="COPY.missingBody">
          <ActionButton :label="COPY.start" kind="primary" @click="raceAgain" />
        </EmptyState>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { IonPage, IonContent } from '@ionic/vue';
import { flagOutline } from 'ionicons/icons';
import { AppHeader, ActionButton, StatTile, EmptyState, prefersReducedMotion } from '../core/ui/index.js';
import { findLap } from '../store.js';

const COPY = {
  title: 'Chequered flag',
  lapTime: 'Lap time',
  position: 'Position',
  newBest: 'That is your best lap yet.',
  tryAgain: 'Pass the cars ahead and you take the win. Brake a little before the hairpins.',
  champ: 'Take the win',
  again: 'Race again',
  start: 'Start lap',
  missingTitle: 'No lap to show',
  missingBody: 'Finish a lap and the result shows here.',
};

const route = useRoute();
const router = useRouter();
const still = prefersReducedMotion();
const lap = computed(() => {
  const l = findLap(route.params.id);
  return l && l.lapTime ? l : null;
});
const won = computed(() => Number(lap.value?.place) === 1);
const isBest = computed(() => lap.value?.status === 'best-lap');
const backTo = router.hasRoute('grid') ? router.resolve({ name: 'grid' }).fullPath : '/';

const toChampagne = () => router.push({ name: 'champagne', params: { id: lap.value.id } });
const raceAgain = () => router.replace({ name: 'grid' });
</script>

<style scoped>
.flag-body { display: grid; gap: var(--sp-4); max-width: 520px; margin: 0 auto; text-align: center; }
.flag {
  height: 150px; border-radius: var(--radius); border: 3px solid var(--ink);
  background: repeating-conic-gradient(var(--race-black) 0 25%, var(--race-white) 0 50%) 0 0 / 36px 36px;
  display: flex; overflow: hidden;
}
.flag span { flex: 1; background: linear-gradient(90deg, transparent, rgba(0,0,0,0.18), transparent); animation: wave 1.6s ease-in-out infinite; }
.flag span:nth-child(2n) { animation-delay: 0.2s; }
.flag span:nth-child(3n) { animation-delay: 0.4s; }
.flag.still span { animation: none; }
@keyframes wave { 0%, 100% { opacity: 0.2; } 50% { opacity: 1; } }
.banner { margin: 0; font-size: var(--text-2xl); font-weight: 900; color: var(--ink); }
.banner--win { color: var(--race-text); background: var(--race-win); border-radius: 12px; padding: var(--sp-2) var(--sp-4); }
.flag-stats { display: grid; grid-template-columns: 1fr 1fr; gap: var(--sp-3); text-align: left; }
.flag-note { margin: 0; color: var(--dim); }
</style>
